import React, { useEffect, useState } from 'react';
import { Button, Input } from '@asigna/kit';
import BigNumber from 'bignumber.js';
import cx from 'classnames';
import { MAX_NOTES_LENGTH, NotesInput } from 'components';
import useAddress from 'hooks/use-address';
// import { useCountTxFee } from 'hooks/use-count-tx-fee';
import useNetwork from 'hooks/use-network';
import useSafe from 'hooks/use-safe';
import { useSafeCalls } from 'hooks/use-safe-call';
import { isEmpty, isUndefined } from 'lodash';
import { AssetListFromBalancesType } from 'utils/asset-list';
import { generateSendTokenTransfer, generateUnsignedStxTransfer } from 'utils/transaction-create';

import { TokenInput } from '../../TokenInput/TokenInput';
import { SetTxFee } from '../components/SetTxFee';

import DropDownList from './DropDownList';
import { NonceInput } from './NonceInput';
import { TransactionCommonDialog } from './TransactionCommonDialog';
import { checkForRecipientError } from './utils';

interface TransactionCommonProps {
  open: boolean;
  tokenList: AssetListFromBalancesType[];
  safeAddress?: string;
  selectedTokenIndex?: number;
  onClose: () => void;
  onContinue: (successful: boolean, message: string) => void;
}

// TODO_R: [RP] rename to InstantSendDialog
// TODO_R: move into SafeDetailsPage/components
// TODO_R: [RCC] [FRS]
// TODO_Y: have a look
export const SendDialog = (props: TransactionCommonProps) => {
  const { open, onClose, onContinue, tokenList, safeAddress, selectedTokenIndex } = props;

  const { safe } = useSafe();
  const [, stacksNetwork, ,] = useNetwork();
  const walletAccount = useAddress();
  // const { countTxGas } = useCountTxFee();

  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [tokenAmount, setTokenAmount] = useState('');
  const [tokenAmountErrorMessage, setTokenAmountErrorMessage] = useState('');
  const [recepientAddress, setRecepientAddress] = useState('');
  const [recepientAddressErrorMessage, setRecepientAddressErrorMessage] = useState('');
  const [selectedToken, setSelectedToken] = useState(
    tokenList[
      selectedTokenIndex != null
        ? selectedTokenIndex
        : // STX first
          0
    ]
  );
  const [nonce, setNonce] = useState<number>();
  const [hasNonceError, setHasNonceError] = useState(false);
  const { nativeSendSTX, nativeSendToken, nativeSendSTX20 } = useSafeCalls();
  const [hasAnError, setHasAnError] = useState(false);
  const [generatedTxHex, setGeneratedTxHex] = useState('');
  const [memo, setMemo] = useState('');
  const [memoError, setMemoError] = useState('');
  const [notesValue, setNotesValue] = useState('');

  // TODO: disable send button if there is no tokens on wallet
  // if (!tokenList.length) return <></>;

  const handleSend = async (fee: number) => {
    if (isUndefined(nonce)) return onContinue(false, 'Transaction is not created');

    const amountInBigNumber = new BigNumber(tokenAmount)
      .times(10 ** selectedToken.decimals)
      .toString();

    let successful = false;

    if (selectedToken?.isSTX20) {
      successful = (
        await nativeSendSTX20(
          nonce,
          recepientAddress,
          `t${selectedToken.symbol}${tokenAmount}`,
          notesValue
        )
      ).isSuccessful;
    } else {
      successful = safeAddress
        ? selectedToken.symbol === 'STX'
          ? (
              await nativeSendSTX(
                nonce,
                recepientAddress,
                Number(amountInBigNumber),
                fee,
                memo,
                notesValue
              )
            ).isSuccessful
          : (
              await nativeSendToken(
                nonce,
                selectedToken.address,
                Number(amountInBigNumber),
                recepientAddress,
                fee,
                selectedToken.ref,
                memo,
                notesValue
              )
            ).isSuccessful
        : false;
    }

    onContinue(
      successful,
      successful
        ? `Transaction is created and added to the Queue tab`
        : 'Transaction is not created'
    );
    onClose();
  };

  const handleNextStepClick = () => {
    // const currentUserOwnerIndex = safe.owners.findIndex((owner) => owner.address === walletAccount);
    if (isUndefined(nonce) || !walletAccount) return;
    const amountBN = new BigNumber(tokenAmount).times(10 ** selectedToken.decimals).toString();

    let generateTx;

    if (selectedToken?.isSTX20) {
      generateTx = () =>
        generateUnsignedStxTransfer(
          nonce,
          walletAccount,
          1,
          stacksNetwork,
          safe.owners.map((owner) => owner.publicKey),
          safe.threshold,
          safe.address,
          0,
          `t${selectedToken.symbol}${tokenAmount}`
        );
    } else {
      generateTx =
        selectedToken.symbol === 'STX'
          ? () =>
              generateUnsignedStxTransfer(
                nonce,
                walletAccount,
                Number(amountBN),
                stacksNetwork,
                safe.owners.map((owner) => owner.publicKey),
                safe.threshold,
                safe.address,
                undefined,
                memo
              )
          : () =>
              generateSendTokenTransfer(
                nonce,
                selectedToken.address,
                Number(amountBN),
                recepientAddress,
                safe,
                stacksNetwork,
                selectedToken.ref,
                undefined,
                memo
              );
    }

    // if (!isAbleToCreateNewFlow(safe.threshold, safe.owners.length, currentUserOwnerIndex)) {
    //   generateTx().then((txHash) => {
    //     const sendWithStandartFee = async () => {
    //       const gas = await countTxGas(txHash);
    //       if (gas)
    //         return handleSend(gas?.estimates.map((estimate) => Number(estimate.fee.amount))[1]);
    //     };
    //
    //     return sendWithStandartFee();
    //   });
    //
    //   return;
    // }

    generateTx().then((txHash) => {
      setGeneratedTxHex(txHash);
      setCurrentStep(2);
    });
  };

  useEffect(
    function validateForErrors() {
      if (!selectedToken) return setHasAnError(false);
      setRecepientAddressErrorMessage('');
      setTokenAmountErrorMessage('');

      const checkForError = () => {
        if (hasNonceError) {
          return true;
        }

        const hasRecipientError = checkForRecipientError(
          recepientAddress,
          safeAddress,
          setRecepientAddressErrorMessage
        );
        if (hasRecipientError) {
          return true;
        }

        if (
          !tokenAmount ||
          !selectedToken ||
          Number(tokenAmount) <= 0 ||
          Number(tokenAmount) > +selectedToken.balance ||
          !recepientAddress
        ) {
          // Number(tokenAmount) > Number(selectedToken?.balance) &&
          //   setTokenAmountErrorMessage('Insufficient balance');

          return false;
        }

        return false;
      };

      setHasAnError(checkForError());
    },
    [tokenAmount, recepientAddress, tokenList, selectedToken, hasNonceError]
  );

  const disabledNext =
    hasAnError ||
    isEmpty(nonce) ||
    hasNonceError ||
    tokenList.length === 0 ||
    memoError.length > 0 ||
    notesValue.length > MAX_NOTES_LENGTH;

  const continueButton = () => {
    if (currentStep === 2) return <></>;

    return (
      <Button
        fullWidth
        colorType="primary"
        onClick={() => handleNextStepClick()}
        disabled={disabledNext}
        message="Next"
      />
    );
  };

  return (
    <TransactionCommonDialog
      open={open}
      title={currentStep === 1 ? 'Instant Send' : 'Choose Fee'}
      button={continueButton()}
      onClose={onClose}
    >
      <div className={cx('w-full flex-col my-32 gap-16', currentStep === 1 ? 'flex' : 'hidden')}>
        {safeAddress && (
          <NonceInput
            value={nonce}
            onChange={(newNonce) => setNonce(newNonce)}
            onError={(hasError) => setHasNonceError(hasError)}
          />
        )}
        <div className="flex flex-col gap-8">
          <DropDownList
            onChange={(address) => setRecepientAddress(address)}
            placeholder="Recipient (address or BNS name)"
            value={recepientAddress}
          />
          {recepientAddressErrorMessage.length > 0 && (
            <div className="text-r12 text-error">{recepientAddressErrorMessage}</div>
          )}
        </div>
        <div className="flex flex-col gap-8">
          <TokenInput
            tokenList={tokenList}
            selectedToken={selectedToken}
            onTokenChange={(token) => setSelectedToken(token)}
            onAmountChange={(amount) => setTokenAmount(amount)}
            inputPlaceholder="Amount"
          />
          {tokenAmountErrorMessage.length > 0 && (
            <div className="text-r12 text-error">{tokenAmountErrorMessage}</div>
          )}
        </div>
        {!selectedToken?.isSTX20 && (
          <div className="flex flex-col gap-8">
            <Input
              placeholder="Memo"
              value={memo}
              onChange={(e) => {
                setMemoError('');
                setMemo(e.target.value);
                e.target.value.length > 34 && setMemoError('Allowed length 34 characters');
              }}
            />
            {memoError.length > 0 && <div className="text-r12 text-error">{memoError}</div>}
          </div>
        )}
        <NotesInput value={notesValue} onChange={setNotesValue} className="mt-0" />
      </div>
      <div className={currentStep === 2 ? 'flex' : 'hidden'}>
        <SetTxFee
          onBackClick={() => setCurrentStep(1)}
          onSend={handleSend}
          transactionHex={generatedTxHex}
        />
      </div>
    </TransactionCommonDialog>
  );
};
